/* eslint-disable prefer-template */
/**
 * Parse and validate Fixed Budget Vessel CSV before upload.
 * Backend template headers: SapCompanyCode,FixedSapVesselCompanyCode,FixedCostCenter
 */

const normalizeHeader = (h) => String(h || '')
  .trim()
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const EXPECTED_HEADERS = 'SapCompanyCode,FixedSapVesselCompanyCode,FixedCostCenter';

const HEADER_ALIASES = {
  sapCompanyCode: ['sap company code', 'sapcompanycode', 'company code', 'companycode'],
  fixedSapVesselCompanyCode: [
    'fixed sap vessel company code',
    'fixedsapvesselcompanycode',
    'sap vessel company code',
    'sapvesselcompanycode',
    'vessel company code',
    'vesselcompanycode'
  ],
  fixedCostCenter: [
    'fixed cost center',
    'fixedcostcenter',
    'cost center',
    'costcenter',
    'cost centre',
    'costcentre',
    'fixed cost centre',
    'fixedcostcentre'
  ]
};

const resolveColumnIndex = (headers) => {
  const map = {};
  Object.keys(HEADER_ALIASES).forEach((key) => {
    const aliases = HEADER_ALIASES[key];
    const collapsedAliases = aliases.map((a) => a.replace(/\s+/g, ''));
    const idx = headers.findIndex((h) => {
      const normalized = normalizeHeader(h);
      const collapsed = normalized.replace(/\s+/g, '');
      return aliases.includes(normalized) || collapsedAliases.includes(collapsed);
    });
    map[key] = idx;
  });
  return map;
};

const parseCsvLine = (line) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
};

const validateFixedBudgetVesselCsv = (fileText) => {
  const errors = [];
  const text = String(fileText || '').replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');

  if (lines.length < 2) {
    return { rows: [], errors: ['No valid data available to upload'], csvBlob: null };
  }

  const headers = parseCsvLine(lines[0]);
  const col = resolveColumnIndex(headers);

  if (col.sapCompanyCode < 0 || col.fixedSapVesselCompanyCode < 0 || col.fixedCostCenter < 0) {
    return {
      rows: [],
      errors: [
        'Error in line number 1. CSV must include SapCompanyCode, '
        + 'FixedSapVesselCompanyCode and FixedCostCenter columns.'
      ],
      csvBlob: null
    };
  }

  const rows = [];
  const costCenterOwners = new Map();

  for (let i = 1; i < lines.length; i += 1) {
    const lineNumber = i + 1;
    const cells = parseCsvLine(lines[i]).map((c) => String(c == null ? '' : c).trim());
    const sapCompanyCode = cells[col.sapCompanyCode] || '';
    const fixedSapVesselCompanyCode = cells[col.fixedSapVesselCompanyCode] || '';
    const fixedCostCenter = cells[col.fixedCostCenter] || '';

    if (!sapCompanyCode) {
      errors.push(
        'Error in line number ' + lineNumber
        + '. The sap company code is either missing or incorrect.'
      );
    }
    if (!fixedSapVesselCompanyCode) {
      errors.push(
        'Error in line number ' + lineNumber
        + '. The fixed sap vessel company code is either missing or incorrect.'
      );
    }
    if (!fixedCostCenter) {
      errors.push(
        'Error in line number ' + lineNumber
        + '. The fixed cost center is either missing or incorrect.'
      );
    } else if (fixedCostCenter.length > 10) {
      errors.push(
        'Error in line number ' + lineNumber
        + '. The fixed cost center is either missing or incorrect.'
      );
    }

    if (
      sapCompanyCode
      && fixedSapVesselCompanyCode
      && fixedCostCenter
      && fixedCostCenter.length <= 10
    ) {
      const ownerKey = fixedSapVesselCompanyCode + '|' + sapCompanyCode;
      const existingOwner = costCenterOwners.get(fixedCostCenter);
      const duplicateCostCenterOwner = costCenterOwners.has(fixedCostCenter)
        && existingOwner !== ownerKey;
      if (duplicateCostCenterOwner) {
        errors.push(
          'Error in line number ' + lineNumber
          + '. The same cost center is added to different vessel and company.'
        );
      } else {
        costCenterOwners.set(fixedCostCenter, ownerKey);
      }
      rows.push({ sapCompanyCode, fixedSapVesselCompanyCode, fixedCostCenter, lineNumber });
    }
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('No valid data available to upload');
  }

  if (errors.length > 0) {
    return { rows: [], errors, csvBlob: null };
  }

  const csvContent = [
    EXPECTED_HEADERS,
    ...rows.map(
      (r) => r.sapCompanyCode + ',' + r.fixedSapVesselCompanyCode + ',' + r.fixedCostCenter
    )
  ].join('\r\n');

  const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  return { rows, errors: [], csvBlob };
};

export { validateFixedBudgetVesselCsv, EXPECTED_HEADERS };
